import { v4 as uuidv4 } from "uuid";
import { put, select, takeEvery } from "redux-saga/effects";
import {
  getBusinessById,
  getConsignmentById,
  getContextByModalType,
  getCurrentSale,
  getCurrentSaleType,
  getReceivalLotById,
  getSaleLotIdsByConsignmentId,
  getSetting,
} from "selectors";
import {
  addConsignment,
  closeConfirmModal,
  closeDispatchingModal,
  createPlaceholderAttachment,
  deleteConsignment,
  navigateToDispatchingModal,
  openConfirmModal,
  openDispatchingModal,
  patchConsignment,
  publishModalResult,
  setSetting,
  updateModalContext,
} from "actions";
import { createModalTitle } from "components/ConfirmDialog";
import toast from "lib/toast";
import {
  closeModal,
  getEditConsignmentHashRoute,
  getReturnTo,
  openAttachments,
} from "lib/navigation";
import {
  CREATE_RECEIVAL_LOT_FROM_CONSIGNMENT_FORM,
  DELETE_CONSIGNMENT_FROM_GENERAL_FORM,
  EDIT_RECEIVAL_LOT_FROM_CONSIGNMENT_FORM,
  TOGGLE_CONSIGNMENT_MODAL_SECTION,
  SUBMIT_CONSIGNMENT_FORM,
} from "constants/actionTypes";
import {
  CreateReceivalLotAction,
  DeleteConfirmedAction,
  deleteConsignmentFromModalConfirmed,
  EditReceivalLotAction,
  PromptDeleteAction,
  SubmitFormAction,
  ToggleConsignmentModalSectionAction,
} from "actions/modals/consignment";
import { ConsignmentModalSection, ModalTypes } from "constants/navigation";
import { getConsignmentPayload } from "lib/consignments";
import { SaleType } from "constants/sale";
import { AttachmentSearchFields } from "constants/attachmentSearch";
import { Settings } from "constants/settings";
import { getApiSale } from "lib/sale";

import history from "appHistory";
import queryString from "query-string";
import { openModalAndWait, storeFormikSnapshot } from "./util";

function* onSubmitForm(action: SubmitFormAction) {
  const { consignmentId, values, isNew } = action;

  const state = (yield select()) as State;

  // create consignment and create new attachment for the created consignment.
  // direct the user to the attachment form so they can edit their new NVD
  const {
    agency_id: agencyId,
    saleLot,
    shouldUpdateVendorDefaultConsigningDeployment,
    rememberAgencyId,
    ...consignmentValues
  } = values;

  const vendor = getBusinessById(consignmentValues.vendor_id)(state);

  if (consignmentValues.useVendorAddressAsPickupAddress) {
    // we can't use the vendor's address AND specify a custom address, so clear it out
    consignmentValues.pickupAddress = null;
  }

  const consignmentPayload = getConsignmentPayload(consignmentValues);
  let tempId = null;

  // Make sure non-string fields that have been edited
  if (!isNew) {
    yield put(
      patchConsignment(consignmentPayload, consignmentId, {
        shouldUpdateVendorDefaultConsigningDeployment,
        changeReason: "Updated from edit consignment",
      }),
    );
  } else {
    tempId = uuidv4();
    const initialBranchId = vendor?.branchId;
    yield put(
      addConsignment(
        tempId,
        consignmentPayload,
        getApiSale(),
        null,
        initialBranchId,
        saleLot,
        agencyId,
        { shouldUpdateVendorDefaultConsigningDeployment },
      ),
    );

    const saleType: SaleType = getCurrentSaleType(state);
    if (saleType === SaleType.PADDOCK) {
      // if we're in a paddock sale and create a new consignment
      // we want to throw the user straight into adding lots for that consignment.
      yield put(publishModalResult(ModalTypes.Consignment, tempId));

      yield put(
        openDispatchingModal(ModalTypes.Consignment, getReturnTo(), null, {
          consignmentId: tempId,
          section: ConsignmentModalSection.SALE_LOTS,
          showSaleLots: true,
        }),
      );
      return;
    }
  }

  const postSubmitAction = getSetting(Settings.createConsignmentFormSubmit)(
    state,
  );

  if (postSubmitAction && postSubmitAction !== "Save") {
    if (postSubmitAction === "Save + NVD") {
      const sale = getCurrentSale(state);
      yield put(
        createPlaceholderAttachment({
          ...sale,
          consignment: tempId,
          agency: agencyId,
        }),
      );

      yield put(publishModalResult(ModalTypes.Consignment, tempId));

      yield put(closeDispatchingModal(ModalTypes.Consignment));

      yield put(setSetting(Settings.agencyId, agencyId));
      yield put(
        setSetting(Settings.attachmentSearch, {
          [AttachmentSearchFields.Consignment]: [tempId],
        }),
      );
      openAttachments();
    } else if (postSubmitAction === "Save + add another") {
      // re-open the consignment modal with empty values
      yield put(closeDispatchingModal(ModalTypes.Consignment));
      const persistentValues = {
        agency_id: rememberAgencyId ? agencyId : undefined,
        rememberAgencyId,
      };

      yield put(
        updateModalContext(ModalTypes.Consignment, {
          persistentValues,
        }),
      );

      // Wait a tick (one frame) so React can unmount the modal and reset internal component state
      yield new Promise(resolve => {
        setTimeout(resolve, 16);
      });

      yield put(
        navigateToDispatchingModal(
          ModalTypes.Consignment,
          {
            consignmentId: null,
            section: ConsignmentModalSection.GENERAL,
          },
          window.location.hash,
        ),
      );
    }
  } else {
    yield put(publishModalResult(ModalTypes.Consignment, tempId));

    yield put(closeDispatchingModal(ModalTypes.Consignment));

    const persistentValues = {
      agency_id: rememberAgencyId ? agencyId : undefined,
      rememberAgencyId,
    };
    yield put(
      updateModalContext(ModalTypes.Consignment, {
        persistentValues,
      }),
    );
  }
}

function* onDeleteConsignment(action: PromptDeleteAction) {
  const { consignmentId } = action;
  const state = (yield select()) as State;

  const consignment: Consignment | null =
    getConsignmentById(consignmentId)(state);

  if (!consignment) {
    toast.error("Could not delete consignment: Consignment not found.");
    return;
  }

  const saleLotIds: string[] | null =
    getSaleLotIdsByConsignmentId(consignmentId)(state);

  if (Array.isArray(saleLotIds) && saleLotIds.length > 0) {
    // Cannot delete a consignment that has lots!
    toast.error(
      "Cannot delete consignment: Consignment cannot be deleted when it has sale lots.",
    );
    return;
  }

  const getCancelAction = () => closeConfirmModal();
  const getDeleteAction = () =>
    deleteConsignmentFromModalConfirmed(consignmentId);

  // Prompt the user to confirm they want to delete the consignment
  yield put(
    openConfirmModal({
      title: createModalTitle("this Consignment"),
      message:
        "Are you sure you want to delete this consignment? This action cannot be undone.",
      actions: [
        {
          dataTour: "cancelDeleteConsignment",
          label: "Cancel",
          onClickAction: getCancelAction,
          secondary: true,
        },
        {
          dataTour: "confirmDeleteConsignment",
          label: "Delete Consignment",
          onClickAction: getDeleteAction,
        },
      ],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- openConfirmModal action type cast
    }) as any,
  );
}

function* onConfirmDeleteConsignment(action: DeleteConfirmedAction) {
  const { consignmentId } = action;
  const state = (yield select()) as State;

  const consignment: Consignment | null =
    getConsignmentById(consignmentId)(state);

  if (!consignment) {
    toast.error("Could not delete consignment: Consignment not found.");
    return;
  }

  closeModal(); // Close the Consignment Modal
  yield put(closeConfirmModal()); // Close the Confirm Deletion Modal
  yield put(deleteConsignment(consignment));
}

function* onToggleSection(action: ToggleConsignmentModalSectionAction) {
  const state = (yield select()) as State;

  const modelContext = getContextByModalType(ModalTypes.Consignment)(state);

  const currentSection: ConsignmentModalSection = modelContext.section;

  // set the next section to be either the one requested, or null (close it) if it's already open
  const newSection = currentSection === action.section ? null : action.section;

  const hash = {
    ...queryString.parse(window.location.hash, {
      parseBooleans: true,
      parseNumbers: true,
      arrayFormat: "comma",
    }),
  };

  const currentArgs = JSON.parse(hash[ModalTypes.Consignment] as string);

  const nextHash = `#${getEditConsignmentHashRoute(
    currentArgs.consignmentId,
    newSection,
    currentArgs.showSaleLots,
    currentArgs.returnTo,
  )}`;

  history.push(nextHash);
}

function* onEditReceivalLot(action: EditReceivalLotAction) {
  const { consignmentId, receivalLotId, formikSnapshot } = action;

  // Store the current state of the form in the modal context
  // Close the current receival lot modal
  const snapshotId = yield storeFormikSnapshot(
    ModalTypes.Consignment,
    formikSnapshot,
  );

  yield openModalAndWait({
    modalType: ModalTypes.EditReceivalLot,
    returnTo: window.location.hash,
    urlArgs: { receivalLotId },
    context: {
      receivalLotId,
      values: { consignmentId },
      formikSnapshot: { touched: { consignmentId: true } },
    },
  });

  // Select the snapshot to restore when the Consignment Modal reopens
  yield put(
    updateModalContext(ModalTypes.Consignment, { values: { snapshotId } }),
  );

  const state = yield select();
  const receivalLot: ReceivalLot | null =
    getReceivalLotById(receivalLotId)(state);

  if (
    // The user deleted the receival lot for the edit form, remove it from the consignment form values
    receivalLot === null
  ) {
    // Only for the next invocation of the form - place the new receival lot id into values
    const receivalLotIds: string[] = formikSnapshot.values.receivalLotIds || [];
    yield put(
      updateModalContext(ModalTypes.Consignment, {
        formikSnapshot: {
          ...formikSnapshot,
          values: {
            ...formikSnapshot.values,
            receivalLotIds: receivalLotIds.filter(
              lotId => lotId !== receivalLotId,
            ),
          },
        },
      }),
    );
  }
}

function* onCreateReceivalLot(action: CreateReceivalLotAction) {
  const { penName, formikSnapshot } = action;

  // Store the current state of the form in the modal context
  // Close the current receival lot modal
  const snapshotId = yield storeFormikSnapshot(
    ModalTypes.Consignment,
    formikSnapshot,
  );

  const { resultAction } = yield openModalAndWait({
    modalType: ModalTypes.EditReceivalLot,
    returnTo: window.location.hash,
    urlArgs: {},
    context: { values: { newPenName: penName, hideConsignmentInput: true } },
  });

  // Apply the snapshot id to restore the snapshot
  yield put(
    updateModalContext(ModalTypes.Consignment, { values: { snapshotId } }),
  );

  const state = yield select();
  const existingValues =
    getContextByModalType(ModalTypes.Consignment)(state).values || null;

  // put the id of the new receival lot into the consignment's form values
  if (resultAction) {
    const newReceivalLotId = resultAction.result;
    // Only for the next invocation of the form - place the new receival lot id into values
    const receivalLotIds: string[] =
      formikSnapshot.values?.receivalLotIds || [];
    receivalLotIds.push(newReceivalLotId);
    yield put(
      updateModalContext(ModalTypes.Consignment, {
        values: { ...existingValues, receivalLotIds },
      }),
    );
  }
}

export function* consignmentModalSagas() {
  yield takeEvery(SUBMIT_CONSIGNMENT_FORM, onSubmitForm);
  yield takeEvery(
    DELETE_CONSIGNMENT_FROM_GENERAL_FORM.ACTION,
    onDeleteConsignment,
  );
  yield takeEvery(
    DELETE_CONSIGNMENT_FROM_GENERAL_FORM.REQUEST,
    onConfirmDeleteConsignment,
  );
  yield takeEvery(TOGGLE_CONSIGNMENT_MODAL_SECTION, onToggleSection);
  yield takeEvery(
    CREATE_RECEIVAL_LOT_FROM_CONSIGNMENT_FORM,
    onCreateReceivalLot,
  );
  yield takeEvery(EDIT_RECEIVAL_LOT_FROM_CONSIGNMENT_FORM, onEditReceivalLot);
}
